import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { AmplitudePageName, track } from "utils/amplitude";
import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right-16.svg";
import { ReactComponent as OutlineChevronLeft } from "assets/svg/outline-chevron-left-16.svg";
import IconButton from "components/icon-button";
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
import getClasses from "utils/getClasses";
import styles from "./MiniCalendar.module.scss";

interface Props {
  analyticsPageName: AmplitudePageName;
}

const ANALYTICS_COMPONENT_NAME = "mini-calendar";

const DATE_FORMAT = "MMM YYYY";

const DAY_LABELS = DAYS_OF_WEEK.map((el) => el.slice(0, 1).toUpperCase());

const ROWS = 6;
const COLS = 7;

const MiniCalendar: React.FC<Props> = ({ analyticsPageName }) => {
  const { currentCalendarTimezone, anchorTime, setAnchorTime } =
    useCalendarTime();
  const onChangeDay = (arg: number) => {
    track(analyticsPageName, [ANALYTICS_COMPONENT_NAME, "day"], "click");
    setAnchorTime(arg);
  };

  const [calendarStartDateValue, setCalendarStartDateValue] = useState(
    moment
      .tz(
        moment.tz(anchorTime, currentCalendarTimezone).startOf("month"),
        currentCalendarTimezone,
      )
      .startOf("week")
      .valueOf(),
  );
  const [monthStartDateValue, setMonthStartDateValue] = useState(
    moment
      .tz(
        moment.tz(anchorTime, currentCalendarTimezone).startOf("month"),
        currentCalendarTimezone,
      )
      .valueOf(),
  );

  const monthEndDateValue = useMemo(
    () =>
      moment
        .tz(
          moment
            .tz(monthStartDateValue, currentCalendarTimezone)
            .endOf("month"),
          currentCalendarTimezone,
        )
        .valueOf(),
    [monthStartDateValue, currentCalendarTimezone],
  );

  // track
  useEffect(() => {
    setCalendarStartDateValue(
      moment
        .tz(
          moment.tz(anchorTime, currentCalendarTimezone).startOf("month"),
          currentCalendarTimezone,
        )
        .startOf("week")
        .valueOf(),
    );

    setMonthStartDateValue(
      moment
        .tz(
          moment.tz(anchorTime, currentCalendarTimezone).startOf("month"),
          currentCalendarTimezone,
        )
        .valueOf(),
    );
  }, [anchorTime, currentCalendarTimezone]);

  const handleChangeMonth = (arg: number) => {
    setCalendarStartDateValue(
      moment
        .tz(monthStartDateValue, currentCalendarTimezone)
        .add(arg, "month")
        .startOf("month")
        .startOf("week")
        .valueOf(),
    );

    setMonthStartDateValue(
      moment
        .tz(monthStartDateValue, currentCalendarTimezone)
        .add(arg, "month")
        .valueOf(),
    );
  };

  const selectedDayValue = useMemo(() => {
    return moment
      .tz(anchorTime, currentCalendarTimezone)
      .startOf("day")
      .valueOf();
  }, [anchorTime, currentCalendarTimezone]);

  const todayValue = useMemo(() => {
    return moment().tz(currentCalendarTimezone).startOf("day").valueOf();
  }, [currentCalendarTimezone]);

  const dateLabel = useMemo(() => {
    return moment
      .tz(monthStartDateValue, currentCalendarTimezone)
      .startOf("month")
      .format(DATE_FORMAT);
  }, [monthStartDateValue, currentCalendarTimezone]);

  return (
    <div className={styles["calendar"]}>
      {/* controls */}
      <div className={styles["controls"]}>
        <div className={styles["date-control"]}>
          <span className={styles["date-label"]}>{dateLabel}</span>
          <IconButton onClick={() => handleChangeMonth(-1)}>
            <OutlineChevronLeft className={"mr-2"} />
          </IconButton>
          <IconButton onClick={() => handleChangeMonth(1)}>
            <OutlineChevronRight />
          </IconButton>
        </div>

        {/* <Button
          variant={"link"}
          onClick={() => onChangeDay && onChangeDay(todayValue)}
        >
          {"Today"}
        </Button> */}
      </div>

      {/* calendar */}
      <div className={styles["calendar-container"]}>
        <table className={styles["calendar"]}>
          <thead className={styles["calendar-header"]}>
            <tr>
              {DAY_LABELS.map((day, i) => (
                <th key={i} scope="col">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array(ROWS)
              .fill(0)
              .map((_, i) => {
                const rowStartDayValue = moment
                  .tz(calendarStartDateValue, currentCalendarTimezone)
                  .add(i * COLS, "day")
                  .valueOf();

                const noAdditionalRow = rowStartDayValue > monthEndDateValue;

                if (noAdditionalRow) {
                  return null;
                }

                return (
                  <tr key={i} className={styles["calendar-body-row"]}>
                    {Array(COLS)
                      .fill(0)
                      .map((_, j) => {
                        const dayValue = moment
                          .tz(rowStartDayValue, currentCalendarTimezone)
                          .add(j, "day")
                          .valueOf();

                        const isPast = dayValue < todayValue;
                        const isToday = dayValue === todayValue;
                        const isActive = dayValue === selectedDayValue;

                        return (
                          <td key={`${i}-${j}`}>
                            <div className={styles["calendar-day-container"]}>
                              <div
                                onClick={() =>
                                  onChangeDay && onChangeDay(dayValue)
                                }
                                className={getClasses(
                                  styles["calendar-day"],
                                  isPast && styles["calendar-day--past"],
                                  isToday && styles["calendar-day--today"],
                                  isActive && styles["calendar-day--active"],
                                )}
                              >
                                {moment
                                  .tz(dayValue, currentCalendarTimezone)
                                  .date()}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MiniCalendar;
